/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Button, Card, Input, Select } from 'antd';
import { Sortable } from 'devextreme-react/sortable';

import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';

function foldSearchText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function sectionFieldMatchesSearch(input, option) {
  const label = option?.label ?? option?.children ?? '';
  return foldSearchText(label).includes(foldSearchText(input));
}

function DragHandle() {
  return (
    <span className="customization-drag-handle">
      <HolderOutlined />
    </span>
  );
}

function forwardReorderIndexes(reorderEvent, onReorder) {
  if (reorderEvent.dropInsideItem === true) {
    return;
  }

  onReorder(reorderEvent.fromIndex, reorderEvent.toIndex);
}

function CustomizationSortable({ itemCount, filter, group, onReorder, children }) {
  return (
    <Sortable
      itemOrientation="vertical"
      dragDirection="vertical"
      allowDropInsideItem={false}
      moveItemOnDrop={false}
      dropFeedbackMode="push"
      allowReordering={itemCount >= 2}
      filter={filter}
      group={group}
      handle=".customization-drag-handle"
      onReorder={(reorderEvent) => forwardReorderIndexes(reorderEvent, onReorder)}
    >
      {children}
    </Sortable>
  );
}

function SectionFieldRow({ fieldName, label }) {
  return (
    <div className="field-customization-row">
      <DragHandle />
      <div className="field-customization-label">{label || fieldName}</div>
    </div>
  );
}

function SectionInnerFields({ section, labelByName, onReorderSectionField }) {
  return (
    <div className="section-inner-fields">
      <CustomizationSortable
        itemCount={section.fieldNames.length}
        filter=".field-customization-row"
        group={`customization-section-fields-${section.sectionId}`}
        onReorder={(fromIndex, toIndex) =>
          onReorderSectionField(section.sectionId, fromIndex, toIndex)
        }
      >
        {section.fieldNames.map((fieldName) => (
          <SectionFieldRow key={fieldName} fieldName={fieldName} label={labelByName[fieldName]} />
        ))}
      </CustomizationSortable>
    </div>
  );
}

function DeleteSectionButton({ sectionId, onDeleteSection }) {
  return (
    <Button
      type="text"
      danger
      icon={<DeleteOutlined />}
      onClick={() => onDeleteSection?.(sectionId)}
    >
      Excluir
    </Button>
  );
}

function SectionHeader({ section, onRename, onDeleteSection }) {
  return (
    <div className="section-customization-row">
      <DragHandle />
      <div className="field-customization-label">
        <Input
          value={section.name}
          onChange={(event) => onRename(section.sectionId, event.target.value)}
        />
      </div>
      <DeleteSectionButton sectionId={section.sectionId} onDeleteSection={onDeleteSection} />
    </div>
  );
}

function SectionCard({
  section,
  fieldOptions,
  labelByName,
  onRename,
  onFieldsChange,
  onReorderSectionField,
  onDeleteSection,
}) {
  return (
    <div className="section-customization-block">
      <SectionHeader section={section} onRename={onRename} onDeleteSection={onDeleteSection} />
      <Select
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        filterOption={sectionFieldMatchesSearch}
        placeholder="Campos da seção"
        value={section.fieldNames}
        options={fieldOptions}
        onChange={(fieldNames) => onFieldsChange(section.sectionId, fieldNames)}
        className="section-fields-select"
      />
      <SectionInnerFields
        section={section}
        labelByName={labelByName}
        onReorderSectionField={onReorderSectionField}
      />
    </div>
  );
}

function CreateSectionRow({ sectionName, onSectionNameChange, onCreate }) {
  return (
    <div className="section-create-row">
      <Input
        value={sectionName}
        placeholder="Nome da seção"
        onChange={(event) => onSectionNameChange(event.target.value)}
        onPressEnter={onCreate}
      />
      <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
        Criar seção
      </Button>
    </div>
  );
}

function buildSectionCard(
  section,
  {
    fieldOptions,
    labelByName,
    onRenameSection,
    onSectionFieldsChange,
    onReorderSectionField,
    onDeleteSection,
  }
) {
  return (
    <SectionCard
      key={section.sectionId}
      section={section}
      fieldOptions={fieldOptions}
      labelByName={labelByName}
      onRename={onRenameSection}
      onFieldsChange={onSectionFieldsChange}
      onReorderSectionField={onReorderSectionField}
      onDeleteSection={onDeleteSection}
    />
  );
}

function SectionsListSortable({ sections, onReorderSection, ...sectionCardBindings }) {
  return (
    <CustomizationSortable
      itemCount={sections.length}
      filter=".section-customization-block"
      group="customization-sections"
      onReorder={onReorderSection}
    >
      {sections.map((section) => buildSectionCard(section, sectionCardBindings))}
    </CustomizationSortable>
  );
}

function SectionsCustomizationPanel({ onCreateSection, ...sectionListProps }) {
  const [sectionName, setSectionName] = useState('');

  const handleCreate = () => {
    const created = onCreateSection(sectionName);
    if (created) {
      setSectionName('');
    }
  };

  return (
    <Card title="Customização de seções" className="customization-card">
      <CreateSectionRow
        sectionName={sectionName}
        onSectionNameChange={setSectionName}
        onCreate={handleCreate}
      />
      <SectionsListSortable {...sectionListProps} />
    </Card>
  );
}

export default SectionsCustomizationPanel;
